import React, { useState, useEffect, FunctionComponent } from "react"
import { IconContext } from "react-icons/lib"
import Spinner from "@murphy-frontend/web-core/components/Spinner"
import AddContactGroup from "./components/AddContactGroup"
import { useGetContactGroupContacts, useGetContactGroups, useGetContactGroupUsers } from "@murphy-frontend/common/api/ContactGroups/Queries"
import { useDeleteContactGroup } from "@murphy-frontend/common/api/ContactGroups/Mutations"
import { useModal } from "@murphy-frontend/web-core/contexts/ModalContext"
import { ModalConfiguration } from "@murphy-frontend/web-core/components/Modal"
import { useUserPreferences } from "../../common/contexts/UserPreferencesContext"
import { useCustomer } from "../../common/contexts/CustomerContext"
import ComponentWrapper from "../../common/components/ComponentWrapper"
import { AiOutlineDelete, AiOutlinePlus } from "react-icons/ai"
import { RiArrowDownSLine, RiArrowUpSLine } from "react-icons/ri"
import { useGetUsers } from "@murphy-frontend/common/api/Users/Queries"
import { useGetContacts } from "../../common/api/Contacts/queries"
import { ContactListTable } from "../Contacts/components/ContactListTable"
import { EventTypes } from '@murphy-frontend/common/enums';
import { useQueryClient } from '@tanstack/react-query';
import queryKeys from "@murphy-frontend/common/api/ContactGroups/QueryKeys"

import { HubConnection, HubConnectionState } from "@microsoft/signalr"
import { createContactGroupConnection } from "../../common/services/signalrService"
import { useHub } from "../../common/hooks/useHub"
import { useClientMethod } from "../../common/hooks/useClientMethod"

const ContactGroups: FunctionComponent = () => {
	const { openModal, closeModal } = useModal()
	const { translations } = useUserPreferences()
	const { customer } = useCustomer()
	const queryClient = useQueryClient();

	const [error, setError] = useState(null)
	const [contacts, setContacts] = useState([])
	const [users, setUsers] = useState([]);
	const [filteredContacts, setFilteredContacts] = useState([]);
	const [id, setId] = useState(-1);
	const [isLoading, setIsLoading] = useState(true);
	const [contactGroups, setContactGroups] = useState([])
	const [contactGroupContacts, setContactGroupContacts] = useState([])
	const [contactGroupUsers, setContactGroupUsers] = useState([])
	const [contactGroupUsersAndContacts, setContactGroupUsersAndContacts] = useState([])
	const [filteredContactGroups, setFilteredContactGroups] = useState([])

	const [hubConnection, setHubConnection] = useState<null | HubConnection | undefined>(null);

	// === SignalR integration start ===

	useEffect(() => {
		const signalRConnection = createContactGroupConnection(customer.Id);
		setHubConnection(signalRConnection);
	}, []);

	const { hubConnectionState, error: signalRError } = useHub(hubConnection);

	useClientMethod(hubConnection, customer.Id, (event) => {
		const eventType = event.EventType;
		const data = event.Payload;

		switch (eventType) {
			case EventTypes.Add:
				// Update contact groups list
				queryClient.setQueryData(
					queryKeys.list(customer.Id),
					(oldData: any[]) => {
						// Ensure we're not adding a duplicate
						const exists = oldData.some(group => group.ID === data.ID);
						return exists ? oldData : [...oldData, data];
					}
				);
				break;

			case EventTypes.Deleted:
				// Remove deleted group from contact groups list
				queryClient.setQueryData(
					queryKeys.list(customer.Id),
					(oldData: any[]) => oldData.filter(group => group.ID !== data.ID)
				);

				// Invalidate specific group details
				queryClient.invalidateQueries({
					queryKey: queryKeys.detail(data.ID),
					exact: true
				});
				break;

			case EventTypes.Update:
				// Update contact groups list
				queryClient.setQueryData(
					queryKeys.list(customer.Id),
					(oldData: any[]) => oldData.map(group =>
						group.ID === data.ID
							? { ...group, ...data, MemberCount: data.MemberCount }
							: group
					)
				);

				// Invalidate specific group details
				queryClient.invalidateQueries({
					queryKey: queryKeys.detail(data.ID),
					exact: true
				});

				// Invalidate group users and contacts
				queryClient.invalidateQueries({
					queryKey: queryKeys.users(data.ID),
					exact: true
				});

				queryClient.invalidateQueries({
					queryKey: queryKeys.contacts(data.ID),
					exact: true
				});
				break;
		}
	});

	useEffect(() => {
		const loading = hubConnectionState !== HubConnectionState.Connected;
		setIsLoading(loading);
	}, [hubConnectionState]);

	useEffect(() => {
		if (signalRError) {
			console.error(signalRError);
		}
	}, [signalRError]);

	// === SignalR integration end ===

	const {
		isLoading: isLoadingContactGroups,
		isError: isErrorContactGroups,
		data: dataContactGroups,
		error: errorContactGroups
	} = useGetContactGroups(customer.Id)

	const {
		isLoading: isLoadingUsers,
		isError: isErrorUsers,
		data: dataUsers,
		error: errorUsers
	} = useGetUsers(customer.Id)

	const {
		isLoading: isLoadingContacts,
		isError: isErrorContacts,
		data: dataContacts,
		error: errorContacts
	} = useGetContacts(customer.Id)

	const {
		isLoading: isLoadingContactGroupUsers,
		isError: isErrorContactGroupUsers,
		data: dataContactGroupUsers,
		error: errorContactGroupusers
	} = useGetContactGroupUsers(id)

	const {
		isLoading: isLoadingContactGroupContacts,
		isError: isErrorContactGroupContacts,
		data: dataContactGroupContacts,
		error: errorContactGroupContacts
	} = useGetContactGroupContacts(id)

	const {
		mutate: delMutate,
		isPending
	} = useDeleteContactGroup()

	const onGetContactGroupsSuccess = data => {
		const mappedData = data.map(p => ({
			name: p.Name,
			count: p.MemberCount,
			id: p.ID
		}))

		mappedData.sort(
			(a, b) => (a.name?.toUpperCase() > b.name?.toUpperCase()) - (a.name?.toUpperCase() < b.name?.toUpperCase())
		)

		setFilteredContactGroups(mappedData)
		setContactGroups(mappedData)
		setIsLoading(false)
	}

	useEffect(() => {
		if (dataContactGroups) {
			onGetContactGroupsSuccess(dataContactGroups)
		}
	}, [dataContactGroups])

	const onGetUsersSuccess = data => {
		const mappedData = data.map(p => ({
			id: p.CustomerUserId?.toUpperCase(),
			name: p.Username,
			email: p.Email,
			mobile: p.Phonenumber,
			type: "user"
		}))

		mappedData.sort((a, b) => a.name - b.name)
		setUsers(mappedData)
	}

	useEffect(() => {
		if (!isLoadingUsers) {
			onGetUsersSuccess(dataUsers)
		}
	}, [dataUsers])

	const onGetContactsSuccess = data => {
		const mappedData = data.map(p => ({
			id: p.ID,
			name: p.Name,
			email: p.Email,
			mobile: p.MobileNr,
			type: "contact",
			selectedOptions: p.SelectedOptions.map(q => ({
				id: q.ID,
				value: q.Value,
				optionSetId: q.OptionSetId,
				optionSetName: q.OptionSetName
			}))
		}))

		mappedData.sort((a, b) => a.name - b.name)
		setContacts(mappedData)
	}

	useEffect(() => {
		if (!isLoadingContacts) {
			onGetContactsSuccess(dataContacts)
		}
	}, [dataContacts])

	const onClickNewGroup = () => {
		const modalConf: ModalConfiguration = {
			title: `${translations.createcontactgroup}`,
			body: (
				<AddContactGroup
					allContacts={[...contacts, ...users]}
					translations={translations}
					customerId={customer.Id}
					onSuccess={() => closeModal()}
					onFail={() => closeModal()}
					onCancel={() => closeModal()}
				/>
			),
			hideButton: true,
			type: "info",
			position: "center"
		}

		openModal(modalConf)
	}

	const onEditGroup = (item) => {
		const modalConf: ModalConfiguration = {
			title: `${translations.editcontactgroup}`,
			body: (
				<AddContactGroup
					contacts={contactGroupUsersAndContacts}
					allContacts={filteredContacts}
					translations={translations}
					customerId={customer.Id}
					onSuccess={() => closeModal()}
					onFail={() => closeModal()}
					onCancel={() => closeModal()}
					groupId={item.id}
					groupName={item.name}
				/>
			),
			hideButton: true,
			type: "info",
			position: "center"
		}

		openModal(modalConf)
	}

	const onClickDeleteContactGroup = contactGroupId => {
		const contactGroupToEdit = contactGroups.filter(p => p.id === contactGroupId)[0]
		const modalConf: ModalConfiguration = {
			title: `${translations.delete} ${translations.contactgroup}`,
			body: <DeleteConfirmationBody name={contactGroupToEdit.name} />,
			hideButton: false,
			buttonText: translations.delete,
			type: "warning",
			position: "center",
			okCallback: () => delMutate(contactGroupId)
		}

		openModal(modalConf)
	}

	useEffect(() => {
		if (!isLoadingContactGroupContacts) {
			onGetContactGroupContactsSuccess(dataContactGroupContacts)
		}
	}, [dataContactGroupContacts])

	const onGetContactGroupContactsSuccess = data => {
		const mappedData = data?.map(p => ({
			name: p.Name,
			id: p.ID,
			email: p.Email,
			isAdded: true,
			mobile: p.MobileNr,
			type: "contact",
			selectedOptions: p.SelectedOptions.map(q => ({
				id: q.ID,
				value: q.Value,
				optionSetId: q.OptionSetId,
				optionSetName: q.OptionSetName
			}))
		})) || [];

		mappedData.sort((a, b) => a.name - b.name)
		setContactGroupContacts(mappedData)
	}

	useEffect(() => {
		if (!isLoadingContactGroupUsers) {
			onGetContactGroupUsersSuccess(dataContactGroupUsers)
		}
	}, [dataContactGroupUsers])

	const onGetContactGroupUsersSuccess = data => {
		const mappedData = data?.map(p => ({
			name: p.UserName,
			id: p.ID,
			isAdded: true,
			customeruserid: p.CustomerUserId?.toUpperCase(),
			email: p.Email,
			mobile: p.MobilePhone,
			type: "user"
		})) || [];

		mappedData.sort((a, b) => a.name - b.name)
		setContactGroupUsers(mappedData)
	}

	useEffect(() => {
		let contactsNotInGroup = contacts.filter(p => !contactGroupContacts.map(q => q.id).includes(p.id))

		let usersNotInGroup = users.filter(p => !contactGroupUsers.map(q => q.customeruserid).includes(p.id))

		const mergedUsersAndContactsNotInGroup = [...contactsNotInGroup, ...usersNotInGroup]
		mergedUsersAndContactsNotInGroup.sort(
			(a, b) => (a.name?.toUpperCase() > b.name?.toUpperCase()) - (a.name?.toUpperCase() < b.name?.toUpperCase())
		)

		setFilteredContacts(mergedUsersAndContactsNotInGroup)

		const mergedUsersAndContactsInGroup = [...contactGroupContacts, ...contactGroupUsers]
		mergedUsersAndContactsInGroup.sort(
			(a, b) => (a.name?.toUpperCase() > b.name?.toUpperCase()) - (a.name?.toUpperCase() < b.name?.toUpperCase())
		)
		setContactGroupUsersAndContacts(mergedUsersAndContactsInGroup)
	}, [contactGroupContacts, contactGroupUsers])

	useEffect(() => {
		if (error) {
			const modalConf: ModalConfiguration = {
				title: "Error",
				body: <ErrorMessage />,
				buttonText: "Ok",
				type: "error",
				okCallback: () => {
					setError(null)
				}
			}

			openModal(modalConf)
		}
	}, [error])

	if (isLoading || isPending || isLoadingContactGroups) {
		return (
			<ComponentWrapper active={2} subMenuActive={4}>
				<div className='spinner-container-global-center'>
					<Spinner />
				</div>
			</ComponentWrapper>
		)
	}

	function ErrorMessage() {
		return <p>{error}</p>
	}

	function DeleteConfirmationBody({ name }) {
		return (
			<p>
				{translations.delete} <strong>{name}</strong> ?
			</p>
		)
	}

	return (
		<ComponentWrapper active={2} subMenuActive={4}>
			<div style={{ display: 'flex', paddingLeft: 20, paddingRight: 20, marginTop: 20, flexDirection: 'column', width: '90rem' }}>
				<div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '78rem', alignItems: 'center' }}>
					<h1>{translations.contactgroups}</h1>
					<div onClick={onClickNewGroup} style={{ cursor: 'pointer', height: 40, width: '14rem', display: 'flex', borderWidth: 1, flexDirection: 'row', backgroundColor: '#FFFFFF', borderColor: '#00B2A9', borderStyle: 'solid', alignItems: 'center', paddingLeft: 10, paddingRight: 10, paddingBottom: 10, paddingTop: 10, borderRadius: 5 }}>
						<IconContext.Provider value={{ className: 'btn-icon' }}>
							<AiOutlinePlus color="#00B2A9" />
						</IconContext.Provider>
						<span style={{ marginLeft: 10, color: "#00B2A9" }}>{translations.createcontactgroup || "Create new group"}</span>
					</div>
				</div>
				<div style={{ width: '80rem' }}>
					{filteredContactGroups.map(item => <div style={{ borderColor: "#EBEBEB", borderWidth: 1, borderStyle: 'solid', borderRadius: 5, display: 'flex', marginBottom: 10, flexDirection: 'column', backgroundColor: 'white', paddingRight: 10, paddingLeft: 10, paddingBottom: 10, paddingTop: 10, justifyContent: "center" }}>
						<div style={{ flexDirection: 'row', display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }} onClick = {()=> id === -1 ? setId(item.id) : setId(-1)}>
							<div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
								<label>{item.name} ({item.count})</label>
								<div onClick={() => onClickDeleteContactGroup(item.id)} style={{ marginLeft: 10 }}>
									<IconContext.Provider value={{ className: 'dropdown-icon' }} >
										<AiOutlineDelete size={20} />
									</IconContext.Provider>
								</div>
							</div>
							{item.id === id ? <RiArrowUpSLine/> : <RiArrowDownSLine/>}
						</div>
						{item.id === id && <ContactListTable
							contacts={contactGroupUsersAndContacts}
						/>}
						{item.id === id && <div onClick={() => onEditGroup(item)} style={{ cursor: 'pointer', height: 40, display: 'flex', borderWidth: 1, width: '12rem', flexDirection: 'row', backgroundColor: '#FFFFFF', borderColor: '#00B2A9', borderStyle: 'solid', alignItems: 'center', paddingLeft: 10, paddingRight: 10, paddingBottom: 10, paddingTop: 10, borderRadius: 5 }}>
							<IconContext.Provider value={{ className: 'btn-icon' }}>
								<AiOutlinePlus color="#00B2A9" />
							</IconContext.Provider>
							<span style={{ marginLeft: 10, color: "#00B2A9" }}>{translations.editcontactgroup || "Edit group"}</span>
						</div>}
					</div>)}
				</div>
			</div>
		</ComponentWrapper>
	)
}

export default ContactGroups